import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Landmark, 
  Plus, 
  MoreVertical,
  Pencil,
  Trash2,
  ArrowUpRight,
  ArrowDownLeft,
  CreditCard,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankAccounts = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [banks, setBanks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankFormData, setBankFormData] = useState({});
  const [paymentFormData, setPaymentFormData] = useState({});

  const accountTypeLabels = {
    checking: t('pages.bankAccounts.checking'),
    savings: t('pages.bankAccounts.savings'),
    credit: t('pages.bankAccounts.creditCard')
  };

  const accountTypeIcons = {
    checking: <Building2 className="h-5 w-5" />,
    savings: <Landmark className="h-5 w-5" />,
    credit: <CreditCard className="h-5 w-5" />
  };

  useEffect(() => {
    fetchBanks();
    fetchPayments();
  }, []);

  const fetchBanks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts/banks`);
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts/payments`);
      setPayments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveBank = async () => {
    try {
      if (editingBank) {
        await axios.put(`${API_URL}/api/accounts/banks/${editingBank.id}`, bankFormData);
        toast.success(t('pages.bankAccounts.bankUpdated'));
      } else {
        await axios.post(`${API_URL}/api/accounts/banks`, bankFormData);
        toast.success(t('pages.bankAccounts.bankAdded'));
      }
      setShowBankDialog(false);
      setEditingBank(null);
      setBankFormData({});
      fetchBanks();
    } catch (err) {
      toast.error(t('pages.bankAccounts.failedToSave'));
    }
  };

  const handleSavePayment = async () => {
    try {
      await axios.post(`${API_URL}/api/accounts/payments`, paymentFormData);
      toast.success(t('pages.bankAccounts.paymentRecorded'));
      setShowPaymentDialog(false);
      setPaymentFormData({});
      fetchBanks();
      fetchPayments();
    } catch (err) {
      toast.error(t('pages.bankAccounts.failedToRecord'));
    }
  };

  const handleDeleteBank = async (id) => {
    if (window.confirm(t('pages.bankAccounts.confirmDelete'))) {
      try {
        await axios.delete(`${API_URL}/api/accounts/banks/${id}`);
        toast.success(t('pages.bankAccounts.bankDeleted'));
        fetchBanks();
      } catch (err) {
        toast.error(t('pages.bankAccounts.failedToDelete'));
      }
    }
  };

  const openEditBank = (bank) => {
    setEditingBank(bank);
    setBankFormData({ ...bank });
    setShowBankDialog(true);
  };

  const openNewBank = () => {
    setEditingBank(null);
    setBankFormData({ account_type: 'checking', currency: 'USD', current_balance: 0 });
    setShowBankDialog(true);
  };

  const openNewPayment = (bankId) => {
    setPaymentFormData({ bank_account_id: bankId, type: 'incoming', payment_method: 'bank_transfer', currency: 'USD' });
    setShowPaymentDialog(true);
  };

  const totalBalance = banks.reduce((sum, b) => sum + (b.current_balance || 0), 0);

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.bankAccounts.title')}</h1>
          <p className="text-muted-foreground">{t('pages.bankAccounts.subtitle')}</p>
        </div>
        <Button onClick={openNewBank} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Plus className="h-4 w-4" /> {t('pages.bankAccounts.addBankAccount')}</Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <Landmark className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('pages.bankAccounts.totalBalance')}</p>
              <p className="text-3xl font-bold">{currencySymbol} {totalBalance.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banks.map(bank => (
          <Card key={bank.id} className="hover:shadow-md transition-shadow">
            <CardHeader className={`flex flex-row items-center justify-between pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  {accountTypeIcons[bank.account_type] || <Landmark className="h-5 w-5" />}
                </div>
                <div>
                  <CardTitle className="text-base">{bank.name}</CardTitle>
                  <p className="text-xs text-muted-foreground">{bank.bank_name}</p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                  <DropdownMenuItem onClick={() => openEditBank(bank)}><Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openNewPayment(bank.id)}><ArrowUpRight className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.bankAccounts.recordPayment')}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteBank(bank.id)}>
                    <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className={`flex justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-muted-foreground">{t('pages.bankAccounts.accountNumber')}</span>
                  <span className="font-mono">****{bank.account_number?.slice(-4)}</span>
                </div>
                <div className={`flex justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-muted-foreground">{t('pages.bankAccounts.accountType')}</span>
                  <Badge variant="outline" className="capitalize">{accountTypeLabels[bank.account_type] || bank.account_type}</Badge>
                </div>
                <div className={`flex justify-between items-center pt-2 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-sm text-muted-foreground">{t('pages.bankAccounts.currentBalance')}</span>
                  <span className="text-xl font-bold">{currencySymbol} {bank.current_balance?.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {banks.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Landmark className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">{t('pages.bankAccounts.noBankAccounts')}</p>
              <Button onClick={openNewBank} variant="outline" className="mt-4">{t('pages.bankAccounts.addBankAccount')}</Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bank Dialog */}
      <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingBank ? t('pages.bankAccounts.editBankAccount') : t('pages.bankAccounts.addBankAccount')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('pages.bankAccounts.accountName')} *</Label>
              <Input value={bankFormData.name || ''} onChange={e => setBankFormData({...bankFormData, name: e.target.value})} placeholder="Business Checking" />
            </div>
            <div className="space-y-2">
              <Label>{t('pages.bankAccounts.bankName')} *</Label>
              <Input value={bankFormData.bank_name || ''} onChange={e => setBankFormData({...bankFormData, bank_name: e.target.value})} placeholder="Chase Bank" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.bankAccounts.accountNumber')} *</Label>
                <Input value={bankFormData.account_number || ''} onChange={e => setBankFormData({...bankFormData, account_number: e.target.value})} placeholder="1234567890" />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.bankAccounts.accountType')}</Label>
                <Select value={bankFormData.account_type || 'checking'} onValueChange={v => setBankFormData({...bankFormData, account_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">{t('pages.bankAccounts.checking')}</SelectItem>
                    <SelectItem value="savings">{t('pages.bankAccounts.savings')}</SelectItem>
                    <SelectItem value="credit">{t('pages.bankAccounts.creditCard')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('pages.bankAccounts.currentBalance')}</Label>
              <Input type="number" value={bankFormData.current_balance || 0} onChange={e => setBankFormData({...bankFormData, current_balance: parseFloat(e.target.value) || 0})} />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowBankDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveBank}>{editingBank ? t('pages.common.edit') : t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('pages.bankAccounts.recordPayment')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('pages.bankAccounts.paymentType')}</Label>
              <Select value={paymentFormData.type || 'incoming'} onValueChange={v => setPaymentFormData({...paymentFormData, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incoming"><span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><ArrowDownLeft className="h-4 w-4 text-emerald-500" /> {t('pages.bankAccounts.incoming')}</span></SelectItem>
                  <SelectItem value="outgoing"><span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><ArrowUpRight className="h-4 w-4 text-red-500" /> {t('pages.bankAccounts.outgoing')}</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.bankAccounts.amount')} *</Label>
                <Input type="number" value={paymentFormData.amount || ''} onChange={e => setPaymentFormData({...paymentFormData, amount: parseFloat(e.target.value) || 0})} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.bankAccounts.date')}</Label>
                <Input type="date" value={paymentFormData.date || ''} onChange={e => setPaymentFormData({...paymentFormData, date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('pages.bankAccounts.paymentMethod')}</Label>
              <Select value={paymentFormData.payment_method || 'bank_transfer'} onValueChange={v => setPaymentFormData({...paymentFormData, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">{t('pages.bankAccounts.bankTransfer')}</SelectItem>
                  <SelectItem value="cash">{t('pages.bankAccounts.cash')}</SelectItem>
                  <SelectItem value="check">{t('pages.bankAccounts.check')}</SelectItem>
                  <SelectItem value="card">{t('pages.bankAccounts.card')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('pages.bankAccounts.reference')}</Label>
              <Input value={paymentFormData.reference || ''} onChange={e => setPaymentFormData({...paymentFormData, reference: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{t('pages.bankAccounts.description')}</Label>
              <Input value={paymentFormData.description || ''} onChange={e => setPaymentFormData({...paymentFormData, description: e.target.value})} />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSavePayment}>{t('pages.bankAccounts.recordPayment')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccounts;
